import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Send, Mail, RefreshCw } from "lucide-react";
import { toast } from "sonner";

type Stage = "t-30" | "t-7" | "t-0" | "t+7";
const STAGE_OFFSETS: { stage: Stage; days: number; label: string }[] = [
  { stage: "t-30", days: -30, label: "30 days before" },
  { stage: "t-7", days: -7, label: "7 days before" },
  { stage: "t-0", days: 0, label: "Renewal day" },
  { stage: "t+7", days: 7, label: "Past due (grace)" },
];

interface MemberRow {
  id: string;
  name: string;
  email: string | null;
  chapter: string;
  next_renewal_date: string | null;
  renewal_source: string;
  membership_status: string;
}

interface LogRow {
  idempotency_key: string;
  status: string;
  created_at: string;
}

interface UpcomingRow {
  key: string;
  member: MemberRow;
  stage: Stage;
  scheduledDate: string;
  sent: boolean;
}

function addDays(base: Date, days: number): string {
  const d = new Date(base);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
  } catch {
    return iso;
  }
}

const AdminRenewalReminders = () => {
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);

  const load = async () => {
    setLoading(true);
    const [memRes, logRes] = await Promise.all([
      supabase
        .from("members")
        .select("id, name, email, chapter, next_renewal_date, renewal_source, membership_status")
        .not("next_renewal_date", "is", null)
        .eq("is_exempt_from_dues", false)
        .in("membership_status", ["good_standing", "grace"])
        .order("next_renewal_date", { ascending: true }),
      supabase
        .from("renewal_reminder_log")
        .select("idempotency_key, status, created_at"),
    ]);

    if (memRes.error) toast.error(memRes.error.message);
    if (logRes.error) toast.error(logRes.error.message);
    setMembers((memRes.data || []) as MemberRow[]);
    setLogs((logRes.data || []) as LogRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const sentKeys = useMemo(() => new Set(logs.filter((l) => l.status === "sent").map((l) => l.idempotency_key)), [logs]);

  const upcoming: UpcomingRow[] = useMemo(() => {
    const today = new Date();
    const startWindow = addDays(today, -14);
    const endWindow = addDays(today, 60);
    const rows: UpcomingRow[] = [];

    for (const m of members) {
      if (!m.next_renewal_date) continue;
      const renewal = new Date(m.next_renewal_date + "T00:00:00Z");
      for (const { stage, days } of STAGE_OFFSETS) {
        const scheduled = new Date(renewal);
        scheduled.setUTCDate(scheduled.getUTCDate() + days);
        const isoDate = scheduled.toISOString().slice(0, 10);
        if (isoDate < startWindow || isoDate > endWindow) continue;
        const key = `renewal-${m.id}-${stage}-${m.next_renewal_date}`;
        rows.push({ key, member: m, stage, scheduledDate: isoDate, sent: sentKeys.has(key) });
      }
    }
    rows.sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
    return rows;
  }, [members, sentKeys]);

  const sendOne = async (row: UpcomingRow) => {
    setSendingKey(row.key);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const projectId = (import.meta as any).env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/send-renewal-reminders?stage=${row.stage}&memberId=${row.member.id}`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Send failed");
      toast.success("Reminder sent");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Send failed");
    } finally {
      setSendingKey(null);
    }
  };

  const sendRenewalPaymentLink = async (row: UpcomingRow) => {
    setSendingKey(`payment-${row.key}`);
    try {
      const { data, error } = await supabase.functions.invoke("create-membership-checkout", {
        body: {
          member_id: row.member.id,
          mode: "renewal",
          success_url: `${window.location.origin}/portal`,
          cancel_url: `${window.location.origin}/portal`,
        },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);

      await supabase.functions.invoke("send-app-email", {
        body: {
          templateName: "renewal-payment-link",
          recipientEmail: row.member.email || "",
          cc: row.member.email ? undefined : [],
          idempotencyKey: `renewal-payment-link-${row.member.id}-${row.member.next_renewal_date}`,
          templateData: {
            firstName: row.member.name?.split(" ")[0] || "",
            chapter: row.member.chapter === "washington" ? "Washington" : row.member.chapter === "union" ? "Union" : "RCN",
            renewalDate: formatDate(row.member.next_renewal_date),
            paymentUrl: (data as any)?.url,
            amountLabel: "$300/year",
          },
        },
      });
      toast.success("Renewal payment link sent");
    } catch (e: any) {
      toast.error(e?.message || "Failed to send payment link");
    } finally {
      setSendingKey(null);
    }
  };

  const runDailyNow = async () => {
    setRunningAll(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const projectId = (import.meta as any).env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/send-renewal-reminders`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token || ""}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Run failed");
      toast.success(`Daily scan complete — ${(json.results || []).filter((r: any) => r.sent).length} sent`);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Run failed");
    } finally {
      setRunningAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Renewal Reminders</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Scheduled reminders for the next 60 days. Sends automatically each morning; chapter presidents are CC'd.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={runDailyNow} disabled={runningAll}>
            {runningAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Run daily scan now
          </Button>
        </CardHeader>
        <CardContent>
          {upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No reminders scheduled in the next 60 days. Make sure members have a joined date so their renewal date is set.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Renewal</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Scheduled</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {upcoming.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell>
                        <div className="font-medium">{r.member.name}</div>
                        <div className="text-xs text-muted-foreground">{r.member.email || "no email"}</div>
                      </TableCell>
                      <TableCell className="capitalize">{r.member.chapter}</TableCell>
                      <TableCell>{formatDate(r.member.next_renewal_date)}</TableCell>
                      <TableCell>
                        {STAGE_OFFSETS.find((s) => s.stage === r.stage)?.label}
                      </TableCell>
                      <TableCell>{formatDate(r.scheduledDate)}</TableCell>
                      <TableCell>
                        {r.sent ? (
                          <Badge variant="secondary">Sent</Badge>
                        ) : (
                          <Badge variant="outline">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!r.member.email || sendingKey === r.key}
                            onClick={() => sendOne(r)}
                          >
                            {sendingKey === r.key ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <><Send className="h-4 w-4 mr-1" /> {r.sent ? "Resend" : "Send now"}</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!r.member.email || sendingKey === `payment-${r.key}`}
                            onClick={() => sendRenewalPaymentLink(r)}
                            title="Send a one-time payment link for the $300 renewal"
                          >
                            {sendingKey === `payment-${r.key}` ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <><Mail className="h-4 w-4 mr-1" /> Pay link</>
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminRenewalReminders;
